/**
 * Product description tabs — local Shopify controller.
 * Replaces the EasyTabs app runtime without network calls or dependencies.
 */
(function () {
  'use strict';

  var WIDGET_SELECTOR = '[data-easytabs-custom]';
  var TAB_SELECTOR = '.easytabs-tab-item';
  var PANEL_SELECTOR = '.easytabs-content-item';
  var instances = new WeakMap();
  var widgetSequence = 0;
  var resizeQueued = false;

  function list(value) {
    return Array.prototype.slice.call(value || []);
  }

  function emit(name, detail) {
    document.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        cancelable: false,
        detail: detail || {},
      }),
    );
  }

  function parseNumber(value, fallback) {
    var number = Number.parseInt(value, 10);
    return Number.isFinite(number) ? number : fallback;
  }

  function slug(value) {
    return String(value || 'widget')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function parts(widget) {
    return {
      holder: widget.querySelector('.easytabs-holder'),
      tablist: widget.querySelector('.easytabs-tabs'),
      tabs: list(widget.querySelectorAll(TAB_SELECTOR)),
      panels: list(widget.querySelectorAll(PANEL_SELECTOR)),
    };
  }

  function indexFromHash(widget, tabs) {
    var hash = window.location.hash.slice(1);
    if (!hash || hash.indexOf('tabs-') !== 0) return -1;

    return tabs.findIndex(function (tab) {
      return tab.dataset.etMarker === hash || tab.dataset.etHandle === hash;
    });
  }

  function loadPanelMedia(panel) {
    if (!panel) return;

    list(panel.querySelectorAll('[data-src]')).forEach(function (element) {
      if (!element.getAttribute('src')) {
        element.setAttribute('src', element.dataset.src);
      }
      element.removeAttribute('data-src');
    });
  }

  function setBeforeActive(elements) {
    elements.forEach(function (element) {
      element.classList.remove('easytabs-before-active');
    });

    elements.forEach(function (element) {
      if (element.classList.contains('easytabs-active') && element.previousElementSibling) {
        element.previousElementSibling.classList.add('easytabs-before-active');
      }
    });
  }

  function setPanelState(panel, tab, active, accordionMode) {
    var accordion = panel.querySelector('.easytabs-accordion-item');
    var content = panel.querySelector('.easytabs-content-holder');

    panel.classList.toggle('easytabs-active', active);
    if (tab) {
      tab.classList.toggle('easytabs-active', active);
      tab.tabIndex = active ? 0 : -1;
      tab.setAttribute('aria-selected', String(active));
    }
    if (accordion) {
      accordion.tabIndex = accordionMode ? 0 : -1;
      accordion.setAttribute('aria-expanded', String(active));
    }
    if (content) {
      content.setAttribute('aria-hidden', String(!active));
      if (active) loadPanelMedia(content);
    }
  }

  function activate(widget, index, options) {
    var state = instances.get(widget);
    if (!state) return;

    var data = parts(widget);
    if (!data.panels.length) return;

    index = Math.max(0, Math.min(index, data.panels.length - 1));
    options = options || {};
    var accordionMode = data.holder.classList.contains('easytabs-accordion');
    var multi = widget.dataset.etAmp === '1';
    var currentPanel = data.panels[index];
    var closing = accordionMode && options.toggle && currentPanel.classList.contains('easytabs-active');

    if (accordionMode && multi) {
      setPanelState(currentPanel, data.tabs[index], !closing, true);
    } else if (closing) {
      setPanelState(currentPanel, data.tabs[index], false, true);
    } else {
      data.panels.forEach(function (panel, panelIndex) {
        setPanelState(panel, data.tabs[panelIndex], panelIndex === index, accordionMode);
      });
      state.activeIndex = index;
    }

    setBeforeActive(data.tabs);
    setBeforeActive(data.panels);

    if (options.focus) {
      var target = accordionMode
        ? currentPanel.querySelector('.easytabs-accordion-item')
        : data.tabs[index];
      if (target) target.focus({ preventScroll: true });
    }

    if (options.scroll && accordionMode) {
      smoothScroll(currentPanel.querySelector('.easytabs-accordion-item') || currentPanel);
    }

    emit('easytabs_tab_changed', {
      widget: widget,
      index: index,
      marker: data.tabs[index] ? data.tabs[index].dataset.etMarker : '',
    });
  }

  function setMode(widget, force) {
    var state = instances.get(widget);
    if (!state) return;

    var data = parts(widget);
    if (!data.holder || !data.tablist) return;

    var forcedAccordion = widget.dataset.etAo === '1';
    var breakpoint = parseNumber(widget.dataset.etMinWidth, 749);
    var autoAccordion = widget.dataset.etAutoAccordion !== '0';
    var availableWidth = Math.max(widget.clientWidth, 1);

    if (force) data.holder.classList.remove('easytabs-accordion');
    var tabsOverflow = data.tablist.scrollWidth > availableWidth + 2;
    var accordionMode =
      forcedAccordion ||
      (breakpoint > 0 && window.innerWidth <= breakpoint) ||
      (autoAccordion && tabsOverflow);

    var changed = data.holder.classList.contains('easytabs-accordion') !== accordionMode;
    data.holder.classList.toggle('easytabs-accordion', accordionMode);
    widget.classList.toggle('easytabs-accordion-view', accordionMode);

    data.panels.forEach(function (panel, index) {
      var accordion = panel.querySelector('.easytabs-accordion-item');
      var content = panel.querySelector('.easytabs-content-holder');
      if (accordion) accordion.tabIndex = accordionMode ? 0 : -1;
      if (content) {
        content.setAttribute('role', accordionMode ? 'region' : 'tabpanel');
        content.setAttribute(
          'aria-labelledby',
          accordionMode ? content.dataset.accLabelledby : content.dataset.tabLabelledby,
        );
      }
      if (!accordionMode && panel.classList.contains('easytabs-active')) {
        state.activeIndex = index;
      }
    });

    if (!accordionMode && !data.panels.some(function (panel) {
      return panel.classList.contains('easytabs-active');
    })) {
      activate(widget, state.activeIndex || 0);
    } else if (changed && !accordionMode) {
      activate(widget, state.activeIndex || 0);
    }
  }

  function removeDuplicateReviewWidgets(widget) {
    if (!widget.dataset.etRs) return;

    try {
      list(document.querySelectorAll(widget.dataset.etRs)).forEach(function (review) {
        if (!review.closest('.easytabs-content-holder')) review.remove();
      });
    } catch (error) {
      console.warn('[product-tabs] Invalid review selector:', widget.dataset.etRs);
    }
  }

  function initKiwi(widget) {
    var target = widget.querySelector('.easytabs-kiwi-holder-ns:not([data-et-kiwi-ready])');
    if (!target || !window.KiwiSizing || !window.KiwiSizing.data) return;

    target.dataset.etKiwiReady = 'true';
    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;
      if (!window.ks || typeof window.ks.loadSizing !== 'function') {
        if (attempts >= 50) window.clearInterval(timer);
        return;
      }

      window.clearInterval(timer);
      window.ks.loadSizing({
        productData: window.KiwiSizing.data,
        options: {
          el: target,
          injectionOrder: 3,
          displayMode: 0,
          calculatorEl: target,
          calculatorDisplayMode: 0,
          calculatorInjectionOrder: 0,
        },
        loaded: function () {
          setMode(widget, true);
        },
      });
    }, 200);
  }

  function setupAccessibility(widget, data) {
    var base = slug(widget.className.match(/tab-widget-([^\s]+)/)?.[1]) || 'widget';
    var widgetId = 'product-tabs-' + base;

    while (document.getElementById(widgetId) && document.getElementById(widgetId) !== widget) {
      widgetId = 'product-tabs-' + base + '-' + ++widgetSequence;
    }
    widget.id = widgetId;

    data.tablist.setAttribute('aria-label', 'Product information');
    data.tabs.forEach(function (tab, index) {
      var panel = data.panels[index];
      if (!panel) return;

      var tabId = widgetId + '-tab-' + index;
      var accordionId = widgetId + '-accordion-' + index;
      var panelId = widgetId + '-panel-' + index;
      var accordion = panel.querySelector('.easytabs-accordion-item');
      var content = panel.querySelector('.easytabs-content-holder');

      tab.id = tabId;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panelId);

      if (accordion) {
        accordion.id = accordionId;
        accordion.setAttribute('role', 'button');
        accordion.setAttribute('aria-controls', panelId);
      }
      if (content) {
        content.id = panelId;
        content.dataset.tabLabelledby = tabId;
        content.dataset.accLabelledby = accordionId;
      }
    });
  }

  function onWidgetClick(event) {
    var widget = event.currentTarget;
    var tab = event.target.closest(TAB_SELECTOR);
    var accordion = event.target.closest('.easytabs-accordion-item');
    var data = parts(widget);

    if (tab && widget.contains(tab)) {
      event.preventDefault();
      activate(widget, data.tabs.indexOf(tab), { focus: false });
      return;
    }

    if (accordion && widget.contains(accordion)) {
      event.preventDefault();
      var panel = accordion.closest(PANEL_SELECTOR);
      activate(widget, data.panels.indexOf(panel), {
        focus: false,
        scroll: widget.dataset.etSta === '1',
        toggle: true,
      });
    }
  }

  function onWidgetKeydown(event) {
    var widget = event.currentTarget;
    var data = parts(widget);
    var tab = event.target.closest(TAB_SELECTOR);
    var accordion = event.target.closest('.easytabs-accordion-item');
    var items;
    var index;
    var next;

    if (tab) {
      items = data.tabs.filter(function (item) {
        return !item.classList.contains('easytabs-kiwi-hidden');
      });
      index = items.indexOf(tab);
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % items.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = items.length - 1;
      if (next !== undefined) {
        event.preventDefault();
        activate(widget, data.tabs.indexOf(items[next]), { focus: true });
      }
      return;
    }

    if (!accordion) return;
    items = data.panels
      .map(function (panel) {
        return panel.querySelector('.easytabs-accordion-item');
      })
      .filter(Boolean);
    index = items.indexOf(accordion);

    if (event.key === 'ArrowDown') next = (index + 1) % items.length;
    if (event.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = items.length - 1;

    if (next !== undefined) {
      event.preventDefault();
      items[next].focus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      accordion.click();
    }
  }

  function initWidget(widget) {
    if (!(widget instanceof Element) || instances.has(widget)) return;

    var data = parts(widget);
    if (!data.holder || !data.tablist || !data.panels.length) return;

    instances.set(widget, { activeIndex: 0 });
    setupAccessibility(widget, data);

    var activeIndex = indexFromHash(widget, data.tabs);
    if (activeIndex < 0) {
      activeIndex = data.panels.findIndex(function (panel) {
        return panel.classList.contains('easytabs-active');
      });
    }
    if (activeIndex < 0) activeIndex = 0;

    widget.addEventListener('click', onWidgetClick);
    widget.addEventListener('keydown', onWidgetKeydown);
    widget.classList.add('easytabs-inited');

    activate(widget, activeIndex);
    setMode(widget, true);
    removeDuplicateReviewWidgets(widget);
    initKiwi(widget);

    emit('easytabs_widget_inited', { widget: widget });
  }

  function initWidgets(root) {
    var widgets = [];

    if (!root) {
      widgets = list(document.querySelectorAll(WIDGET_SELECTOR));
    } else if (root instanceof Element && root.matches(WIDGET_SELECTOR)) {
      widgets = [root];
    } else if (root.querySelectorAll) {
      widgets = list(root.querySelectorAll(WIDGET_SELECTOR));
    } else {
      widgets = list(root).filter(function (item) {
        return item instanceof Element && item.matches(WIDGET_SELECTOR);
      });
    }

    widgets.forEach(initWidget);
  }

  function updateView(target, force) {
    var widgets = [];

    if (!target) widgets = list(document.querySelectorAll(WIDGET_SELECTOR));
    else if (target instanceof Element && target.matches(WIDGET_SELECTOR)) widgets = [target];
    else if (target instanceof Element) widgets = list(target.querySelectorAll(WIDGET_SELECTOR));
    else widgets = list(target);

    widgets.forEach(function (widget) {
      if (instances.has(widget)) setMode(widget, Boolean(force));
    });
  }

  function queueResize() {
    if (resizeQueued) return;
    resizeQueued = true;
    window.requestAnimationFrame(function () {
      resizeQueued = false;
      updateView();
    });
  }

  function smoothScroll(element) {
    if (!element) return;

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var offset = Number(window.easytabsExtraTopOffset || easytabs.extraTopOffset || 0);
    var top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion ? 'auto' : 'smooth' });
  }

  function fixedHeader(selector) {
    function measure() {
      easytabs.extraTopOffset = list(document.querySelectorAll(selector)).reduce(function (height, item) {
        return height + item.offsetHeight;
      }, 0);
    }

    measure();
    window.addEventListener('resize', measure, { passive: true });
  }

  function activateHash(hash, shouldScroll) {
    if (!hash || hash.indexOf('tabs-') !== 0) return false;

    var matched = false;
    list(document.querySelectorAll(WIDGET_SELECTOR)).some(function (widget) {
      var data = parts(widget);
      var index = data.tabs.findIndex(function (tab) {
        return tab.dataset.etMarker === hash || tab.dataset.etHandle === hash;
      });
      if (index < 0) return false;

      activate(widget, index);
      if (shouldScroll) smoothScroll(widget);
      matched = true;
      return true;
    });
    return matched;
  }

  function initPage() {
    if (easytabs.inited) {
      initWidgets(document);
      return;
    }

    emit('easytabs_before_init');
    easytabs.inited = true;
    initWidgets(document);

    window.addEventListener('resize', queueResize, { passive: true });
    window.addEventListener('hashchange', function () {
      activateHash(window.location.hash.slice(1), true);
    });
    document.addEventListener('shopify:section:load', function (event) {
      initWidgets(event.target);
      queueResize();
    });
    document.addEventListener('shopify:section:reorder', queueResize);

    document.addEventListener('click', function (event) {
      var link = event.target.closest('a[href*=\"#tabs-\"]');
      if (!link || link.classList.contains('ui-tabs-anchor')) return;

      var url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (error) {
        return;
      }
      if (url.pathname !== window.location.pathname) return;

      if (activateHash(url.hash.slice(1), true)) event.preventDefault();
    });

    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          list(mutation.addedNodes).forEach(function (node) {
            if (node.nodeType === 1) initWidgets(node);
          });
        });
      }).observe(document.documentElement, { childList: true, subtree: true });
    }

    if ('ResizeObserver' in window) {
      new ResizeObserver(queueResize).observe(document.documentElement);
    }

    emit('easytabs_page_inited');
  }

  var easytabs = {
    __customController: true,
    activeTabHash: window.location.hash.slice(1),
    extraTopOffset: 0,
    inited: false,
    fixedHeader: fixedHeader,
    initPage: initPage,
    initWidget: initWidget,
    initWidgets: initWidgets,
    smoothScroll: smoothScroll,
    updateTabsClasses: function (holder, index) {
      var widget = holder.closest(WIDGET_SELECTOR) || holder;
      activate(widget, index);
    },
    updateView: updateView,
  };

  if (window.easytabs && window.easytabs.__customController) {
    window.easytabs.initWidgets(document);
    return;
  }

  window.easytabs = easytabs;
  window.easytabsScriptStatus = 'loaded';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPage, { once: true });
  } else {
    initPage();
  }
})();
